import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/use-auth';
import { useYearContext } from '../contexts/YearContext';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { YearControls } from '../components/year-controls';
import { YearLockControls } from '../components/year-lock-controls';
import { PDFReportGenerator } from '../components/reports/pdf-report-generator';
import { WordReportGenerator } from '../components/reports/word-report-generator';
import { getQueryFn, apiRequest } from '../lib/queryClient';

export default function Reports() {
  const { user } = useAuth();
  const { 
    baseYear, 
    selectedYear, 
    currentCompany 
  } = useYearContext();

  const handleCSVExport = async () => {
    if (!currentCompany?.id) {
      alert('Please select a company first');
      return;
    }

    try {
      const endpoint = `/api/files/export-emissions-csv/${currentCompany.id}${selectedYear ? `?reporting_year=${selectedYear}` : ''}`;
      
      const response = await apiRequest('GET', endpoint);

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `emissions_export_${new Date().toISOString().split('T')[0]}.csv`;

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('CSV export error:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleExcelExport = async () => {
    if (!currentCompany?.id) {
      alert('Please select a company first');
      return;
    }

    try {
      const endpoint = `/api/files/export-excel-dashboard/${currentCompany.id}${selectedYear ? `?reporting_year=${selectedYear}` : ''}`;
      
      const response = await apiRequest('GET', endpoint);

      if (!response.ok) {
        throw new Error('Failed to export Excel dashboard');
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `dashboard_export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('Excel export error:', error);
      alert('Failed to export Excel dashboard. Please try again.');
    }
  };

  // Initialize feather icons
  useEffect(() => {
    const feather = (window as any).feather as { replace: (opts?: Record<string, unknown>) => void } | undefined;
    feather?.replace();
  }, []);

  // Company selection is now handled by YearContext

  const { data: emissionSummary } = useQuery({
    queryKey: currentCompany?.id && selectedYear ? 
      ['/api/emissions/summary/' + currentCompany.id, { year: selectedYear }] : 
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  const reportData = currentCompany && emissionSummary ? {
    company: currentCompany,
    emissionSummary,
    intensity: {
      per_employee: currentCompany.employees ? emissionSummary.total_emissions / currentCompany.employees : undefined,
      per_area: currentCompany.area ? emissionSummary.total_emissions / currentCompany.area : undefined,
      per_revenue: currentCompany.revenue ? emissionSummary.total_emissions / currentCompany.revenue : undefined,
    },
  } : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Reports & Export</h1>
              <p className="text-gray-600 mt-2">
                Generate comprehensive emission reports and export data
              </p>
            </div>

            {/* Year Context Controls */}
            <div className="mb-8 space-y-3">
              <YearControls 
                showComparison={false} 
                showBaseYear={true}
                showCountryFilter={true}
                compact={false}
              />
              <YearLockControls />
            </div>

            {/* Company Info */}
            {currentCompany && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <i data-feather="building" className="w-5 h-5 mr-2"></i>
                    Company Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-gray-600">Company Name</p>
                      <p className="text-gray-900">{currentCompany.name}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-600">Industry</p>
                      <p className="text-gray-900">{currentCompany.industry_name || currentCompany.industry || 'Not specified'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-600">Country</p>
                      <p className="text-gray-900">
                        {currentCompany.country_name 
                          ? `${currentCompany.country_name}` 
                          : currentCompany.country_code 
                          ? `${currentCompany.country_code}` 
                          : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-600">Base Year</p>
                      <p className="text-gray-900">{baseYear || 'Not set'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Report Generation */}
            {currentCompany ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* PDF Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <i data-feather="file-text" className="w-5 h-5 mr-2"></i>
                    PDF Report Generation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Generate a PDF GHG inventory report with scope totals, base-year comparison,
                      and data quality summary.
                    </p>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Report Contents:</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Company information and reporting year</li>
                        <li>• Scope 1 / 2 / 3 totals (t CO₂e)</li>
                        <li>• Base year vs reporting year comparison</li>
                        <li>• Measured vs estimated entry counts</li>
                      </ul>
                    </div>
                    
                    <div className="flex gap-3 flex-wrap">
                      <PDFReportGenerator companyId={currentCompany?.id} />
                      <WordReportGenerator companyId={currentCompany?.id} />
                    </div>                  </div>
                </CardContent>
              </Card>

              {/* Export Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <i data-feather="download" className="w-5 h-5 mr-2"></i>
                    Data Export Options
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Export your emission data in various formats for analysis and reporting.
                    </p>
                    
                    <div className="space-y-3">
                      <button 
                        onClick={handleCSVExport}
                        className="flex items-center w-full px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <i data-feather="file-minus" className="w-5 h-5 mr-3 text-green-600"></i>
                        <div>
                          <p className="font-medium">Export to CSV</p>
                          <p className="text-sm text-gray-500">Raw emission data for spreadsheet analysis</p>
                        </div>
                      </button>
                      
                      <button 
                        onClick={handleExcelExport}
                        className="flex items-center w-full px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <i data-feather="bar-chart" className="w-5 h-5 mr-3 text-blue-600"></i>
                        <div>
                          <p className="font-medium">Excel Dashboard</p>
                          <p className="text-sm text-gray-500">Formatted Excel file with charts and pivot tables</p>
                        </div>
                      </button>
                      
                      <button className="flex items-center w-full px-4 py-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <i data-feather="code" className="w-5 h-5 mr-3 text-purple-600"></i>
                        <div>
                          <p className="font-medium">JSON Export</p>
                          <p className="text-sm text-gray-500">Machine-readable format for API integration</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  <i data-feather="building" className="w-16 h-16 mx-auto mb-4 text-gray-300"></i>
                  <h3 className="text-lg font-medium mb-2">No Company Selected</h3>
                  <p>Please select a company from the filters above to generate reports.</p>
                </div>
              </div>
            )}

            {/* Report Preview */}
            {emissionSummary && currentCompany && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <i data-feather="eye" className="w-5 h-5 mr-2"></i>
                    Report Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Company Summary */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Company Summary</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-gray-600">Company Name</p>
                          <p>{currentCompany.name}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Industry</p>
                          <p>{currentCompany.industry_name || currentCompany.industry || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Country</p>
                          <p>{currentCompany.country_code ? `${currentCompany.country_code}` : 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Selected Year</p>
                          <p>{selectedYear || 'Not selected'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Emissions Summary */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Emissions Summary</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm font-medium text-green-800">Scope 1 (Direct)</p>
                          <p className="text-2xl font-bold text-green-900">
                            {emissionSummary.scope_totals?.scope1?.toFixed(2) || '0'}
                          </p>
                          <p className="text-sm text-green-600">kg CO2e</p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm font-medium text-blue-800">Scope 2 (Energy)</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {emissionSummary.scope_totals?.scope2?.toFixed(2) || '0'}
                          </p>
                          <p className="text-sm text-blue-600">kg CO2e</p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm font-medium text-purple-800">Scope 3 (Other)</p>
                          <p className="text-2xl font-bold text-purple-900">
                            {emissionSummary.scope_totals?.scope3?.toFixed(2) || '0'}
                          </p>
                          <p className="text-sm text-purple-600">kg CO2e</p>
                        </div>
                      </div>
                    </div>

                    {/* Total and Data Quality */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-2">Total Emissions</h4>
                        <p className="text-3xl font-bold text-gray-900">
                          {emissionSummary.total_emissions?.toFixed(2) || '0'}
                        </p>
                        <p className="text-sm text-gray-500">kg CO2e</p>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">Data Quality</h4>
                        <div className="space-y-1">
                          <p className="text-sm">
                            <span className="font-medium text-green-600">
                              {emissionSummary.breakdown?.measured || 0}
                            </span> measured entries
                          </p>
                          <p className="text-sm">
                            <span className="font-medium text-orange-600">
                              {emissionSummary.breakdown?.estimated || 0}
                            </span> estimated entries
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}